import { useEffect, useState } from "react";
import { Link, useNavigate, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { MailCheck } from "lucide-react";
import api from "../lib/api";
import { useAuth, apiError } from "../lib/auth";
import AuthShell from "../components/auth/AuthShell";
import PasswordInput, { StrengthHint } from "../components/auth/PasswordInput";

const afterAuth = (from) => (from && from.startsWith("/") && !from.startsWith("//") ? from : "/overblik");
const errText = (err) => (err.response ? apiError(err.response.data?.detail) || "Noget gik galt – prøv igen." : "Kunne ikke forbinde. Tjek din internetforbindelse og prøv igen.");

function ErrorBox({ error, testid }) {
  return error ? <div className="notice error" role="alert" data-testid={testid}>{error}</div> : null;
}

function Submit({ busy, label, busyLabel, testid, kind = "primary" }) {
  return (
    <button className={`btn ${kind} big as-submit`} disabled={busy} data-testid={testid}>
      {busy && <span className="as-spin" aria-hidden="true" />}{busy ? busyLabel : label}
    </button>
  );
}

function useSubmit(fn) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { await fn(); } catch (err) { setError(errText(err)); } finally { setBusy(false); }
  };
  return { error, busy, submit, setError };
}

export function LoginPage() {
  const { store, login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const dest = afterAuth(loc.state?.from);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { error, busy, submit } = useSubmit(async () => { await login(email, password); nav(dest, { replace: true }); });
  if (store) return <Navigate to={dest} replace />;
  return (
    <AuthShell>
      <form onSubmit={submit} data-testid="login-form" noValidate>
        <h1>Log ind</h1>
        <p className="as-sub">Velkommen tilbage – log ind for at se jeres tilbud og ordrer.</p>
        {loc.state?.notice && <div className="notice" role="status" data-testid="login-notice">{loc.state.notice}</div>}
        <ErrorBox error={error} testid="login-error" />
        <div className="field">
          <label htmlFor="email">E-mail</label>
          <input id="email" type="email" autoComplete="email" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
        </div>
        <div className="field">
          <div className="as-label-row">
            <label htmlFor="password">Adgangskode</label>
            <Link to="/glemt-adgangskode" state={{ email }} data-testid="forgot-link">Glemt adgangskode?</Link>
          </div>
          <PasswordInput id="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" testid="login-password" />
        </div>
        <Submit busy={busy} label="Log ind" busyLabel="Logger ind…" testid="login-submit" />
        <p className="as-alt">Har du ikke en konto? <Link to="/signup" data-testid="goto-signup-link">Opret butik</Link></p>
        <p className="as-alt as-staff"><Link to="/personale" data-testid="goto-staff-login">Medarbejder? Log ind her</Link></p>
      </form>
    </AuthShell>
  );
}

export function SignupPage() {
  const { store, signup } = useAuth();
  const nav = useNavigate();
  const [values, setValues] = useState({ store_name: "", email: "", password: "", terms: false });
  const { error, busy, submit } = useSubmit(async () => { await signup(values); nav("/overblik", { replace: true }); });
  if (store) return <Navigate to="/overblik" replace />;
  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  return (
    <AuthShell variant="signup">
      <form onSubmit={submit} data-testid="signup-form">
        <h1>Opret butik</h1>
        <p className="as-sub as-free" data-testid="signup-free-note">14 dage gratis – intet kort, ingen binding</p>
        <ErrorBox error={error} testid="signup-error" />
        <div className="field">
          <label htmlFor="store_name">Butikkens navn</label>
          <input id="store_name" required maxLength={80} autoComplete="organization" value={values.store_name} onChange={set("store_name")} placeholder="Fx SuperBrugsen Hjordkær" data-testid="signup-name" />
        </div>
        <div className="field">
          <label htmlFor="email">E-mail</label>
          <input id="email" type="email" required autoComplete="email" value={values.email} onChange={set("email")} data-testid="signup-email" />
        </div>
        <div className="field">
          <label htmlFor="password">Adgangskode</label>
          <PasswordInput id="password" minLength={8} value={values.password} onChange={set("password")} autoComplete="new-password" testid="signup-password" />
          <StrengthHint pw={values.password} testid="signup-strength" />
        </div>
        <label className="check" style={{ marginBottom: 14 }}>
          <input type="checkbox" checked={values.terms} onChange={set("terms")} required data-testid="signup-terms" /> Jeg
          accepterer <Link to="/vilkaar" target="_blank">vilkårene</Link> og{" "}
          <Link to="/databehandleraftale" target="_blank" data-testid="signup-dpa-link">databehandleraftalen</Link>
        </label>
        <Submit busy={busy} label="Opret butik" busyLabel="Opretter…" testid="signup-submit" kind="jatak" />
        <p className="as-alt">
          Har du allerede en konto? <Link to="/login" data-testid="goto-login-link">Log ind</Link> · <Link to="/priser" data-testid="signup-pricing-link">Se priser</Link>
        </p>
      </form>
    </AuthShell>
  );
}

export function ForgotPage() {
  const loc = useLocation();
  const [email, setEmail] = useState(loc.state?.email || "");
  const [sent, setSent] = useState("");
  const { error, busy, submit } = useSubmit(async () => { const r = await api.post("/auth/forgot-password", { email }); setSent(r.data.message); });
  return (
    <AuthShell>
      {sent ? (
        <div className="as-done" data-testid="forgot-sent">
          <MailCheck size={30} />
          <h1>Tjek din mail</h1>
          <p className="as-sub" data-testid="forgot-sent-message">{sent}</p>
          <p className="as-sub">Linket virker i 60 minutter. Kan du ikke finde mailen, så kig i spam.</p>
          <p className="as-alt"><Link to="/login" data-testid="forgot-back-login">Tilbage til log ind</Link></p>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="forgot-form">
          <h1>Glemt adgangskode?</h1>
          <p className="as-sub">Skriv jeres e-mail, så sender vi et link til at vælge en ny adgangskode.</p>
          <ErrorBox error={error} testid="forgot-error" />
          <div className="field">
            <label htmlFor="email">E-mail</label>
            <input id="email" type="email" autoComplete="email" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
          </div>
          <Submit busy={busy} label="Send link" busyLabel="Sender…" testid="forgot-submit" />
          <p className="as-alt"><Link to="/login" data-testid="forgot-login-link">Tilbage til log ind</Link></p>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPage() {
  const nav = useNavigate();
  const token = useSearchParams()[0].get("token") || "";
  const [valid, setValid] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const { error, busy, submit, setError } = useSubmit(async () => {
    const r = await api.post("/auth/reset-password", { token, password: pw });
    nav("/login", { replace: true, state: { notice: r.data.message } });
  });
  useEffect(() => {
    api.get("/auth/reset-password/check", { params: { token } }).then((r) => setValid(r.data.valid)).catch(() => setValid(true));
  }, [token]);
  const onSubmit = (e) => {
    if (pw.length < 8) { e.preventDefault(); return setError("Adgangskoden skal være mindst 8 tegn."); }
    if (pw !== pw2) { e.preventDefault(); return setError("De to adgangskoder er ikke ens."); }
    return submit(e);
  };
  return (
    <AuthShell>
      {valid === false ? (
        <div className="as-done" data-testid="reset-invalid">
          <h1>Linket virker ikke</h1>
          <p className="as-sub">Linket er ugyldigt, udløbet eller allerede brugt. Bed om et nyt link.</p>
          <Link className="btn primary big as-submit" to="/glemt-adgangskode" data-testid="reset-new-link">Send et nyt link</Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} data-testid="reset-form" noValidate>
          <h1>Vælg ny adgangskode</h1>
          <p className="as-sub">Vælg en adgangskode på mindst 8 tegn.</p>
          <ErrorBox error={error} testid="reset-error" />
          <div className="field">
            <label htmlFor="pw">Ny adgangskode</label>
            <PasswordInput id="pw" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" testid="reset-password" />
            <StrengthHint pw={pw} testid="reset-strength" />
          </div>
          <div className="field">
            <label htmlFor="pw2">Gentag adgangskode</label>
            <PasswordInput id="pw2" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" testid="reset-password2" />
          </div>
          <Submit busy={busy} label="Gem ny adgangskode" busyLabel="Gemmer…" testid="reset-submit" />
          <p className="as-alt"><Link to="/login">Tilbage til log ind</Link></p>
        </form>
      )}
    </AuthShell>
  );
}
